import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  listDAppConfigs,
  updateDAppConfig,
  deleteDAppConfig,
  listMappings,
  listPolicies,
  addMappings,
  updateMapping,
  deleteMapping,
  DAppConfig,
  MappingRecord,
  PolicyRecord,
  AbiEntry,
  convertMethodSignatureToPrototype,
} from '../services/trustline';
import { getNetworkByChainId, isStellarNetwork, NetworkConfig } from '../config/networks';
import { fetchStellarContractFunctions, inspectStellarProtectedContract } from '../services/stellar';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import { engineTypeBadgeClass } from '../utils/engineBadges';
import DashIconButton from '../components/DashIconButton';
import FormattedMethodSignature from '../components/FormattedMethodSignature';
import ContractAddress from '../components/ContractAddress';
import OnChainConfigPanel from '../components/OnChainConfigPanel';
import { validateContractAbi } from '../utils/abiValidation';
import { buildActionIdMethodLookup, computeActionId } from '../utils/actionId';
import { getColorForPolicyId } from '../utils/policyColors';
import '../components/layout/DashboardLayout.css';

function extractEvmMethods(abi: AbiEntry[]): string[] {
  return abi
    .filter(
      (item) =>
        item.type === 'function' &&
        item.stateMutability !== 'view' &&
        item.stateMutability !== 'pure'
    )
    .map((item) => {
      const params =
        item.inputs?.map((input) => `${input.type} ${input.name || ''}`.trim()).join(', ') || '';
      return `${item.name}(${params})`;
    });
}

const DAppDetailPage: React.FC = () => {
  const { chainId: chainIdParam, contractAddress: addressParam } = useParams<{
    chainId: string;
    contractAddress: string;
  }>();
  const chainId = chainIdParam ? decodeURIComponent(chainIdParam) : '';
  const contractAddress = addressParam ? decodeURIComponent(addressParam) : '';
  const navigate = useNavigate();

  const [dApp, setDApp] = useState<DAppConfig | null>(null);
  const [mappings, setMappings] = useState<MappingRecord[]>([]);
  const [policies, setPolicies] = useState<PolicyRecord[]>([]);
  const [methods, setMethods] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [interfaceLoading, setInterfaceLoading] = useState(false);
  const [interfaceError, setInterfaceError] = useState<string | null>(null);
  const [firewallTargetAddress, setFirewallTargetAddress] = useState<string | null>(null);
  const [validationEngineAddress, setValidationEngineAddress] = useState<string | null>(null);
  const [network, setNetwork] = useState<NetworkConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [abiText, setAbiText] = useState('');
  const [abiError, setAbiError] = useState('');
  const [showAbiEditor, setShowAbiEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingName, setSavingName] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [policyDrafts, setPolicyDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const [deleteMappingTarget, setDeleteMappingTarget] = useState<MappingRecord | null>(null);
  const [deletingMapping, setDeletingMapping] = useState(false);
  const [deleteMappingError, setDeleteMappingError] = useState<string | null>(null);

  const resolveInterface = useCallback(async (config: DAppConfig) => {
    setInterfaceLoading(true);
    setInterfaceError(null);
    setFirewallTargetAddress(null);
    setValidationEngineAddress(null);
    setNetwork(null);
    try {
      const numericChain = Number(config.chainId);
      const engine =
        config.type === 'stellar' ? 'stellar' : config.type === 'evm' ? 'evm' : undefined;
      const net = Number.isNaN(numericChain)
        ? undefined
        : getNetworkByChainId(numericChain, engine as 'evm' | 'stellar' | undefined);

      if (config.type === 'stellar') {
        if (!net || !isStellarNetwork(net)) {
          setInterfaceError('Unknown Stellar network for this chainId — cannot fetch SEP-48.');
          setMethods([]);
          return;
        }
        setNetwork(net);
        // Same as onboarding: if firewall, SEP-48 from target; VE from onboarded (firewall) address.
        const inspection = await inspectStellarProtectedContract(net, config.contractAddress);
        setFirewallTargetAddress(inspection.firewallTargetAddress);
        setValidationEngineAddress(inspection.validationEngineAddress);
        const funcs = await fetchStellarContractFunctions(net, inspection.specContractAddress);
        setMethods(funcs.map((f) => f.displaySignature || f.prototype));
      } else {
        if (net) setNetwork(net);
        // EVM VE + on-chain config are resolved via public RPC inside OnChainConfigPanel.
        if (config.abi && config.abi.length > 0) {
          setMethods(extractEvmMethods(config.abi));
          setAbiText(JSON.stringify(config.abi, null, 2));
          setAbiError('');
        } else {
          setMethods([]);
          setAbiText('');
          setShowAbiEditor(true);
          setInterfaceError(
            'ABI is missing for this EVM dApp. Paste a valid ABI and save to enable method mapping.'
          );
        }
      }
    } catch (err: any) {
      setInterfaceError(err.message || 'Failed to resolve contract interface');
      setMethods([]);
      setFirewallTargetAddress(null);
      setValidationEngineAddress(null);
    } finally {
      setInterfaceLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [dAppsRes, mappingsRes, policiesRes] = await Promise.all([
      listDAppConfigs(),
      listMappings({ chainId, contractAddress }),
      listPolicies(),
    ]);

    if (!dAppsRes.success) {
      setError(dAppsRes.error || 'Failed to load dApp');
      setLoading(false);
      return;
    }

    const found = (dAppsRes.data?.dAppConfigs || []).find(
      (d) =>
        String(d.chainId) === String(chainId) &&
        d.contractAddress.toLowerCase() === contractAddress.toLowerCase()
    );

    if (!found) {
      setError('dApp not found');
      setLoading(false);
      return;
    }

    setDApp(found);
    setName(found.name);
    if (found.abi) {
      setAbiText(JSON.stringify(found.abi, null, 2));
    }

    if (mappingsRes.success && mappingsRes.data?.mappings) {
      setMappings(mappingsRes.data.mappings);
    }
    if (policiesRes.success && policiesRes.data?.policies) {
      setPolicies(policiesRes.data.policies);
    }

    setLoading(false);
    await resolveInterface(found);
  }, [chainId, contractAddress, resolveInterface]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAbiChange = (value: string) => {
    setAbiText(value);
    if (!value.trim()) {
      setAbiError('');
      return;
    }
    const result = validateContractAbi(value);
    setAbiError(result.valid ? '' : result.error || 'Invalid ABI');
  };

  const handleSaveName = async () => {
    if (!dApp || !name.trim()) return;
    setSavingName(true);
    setError(null);
    setSuccess(null);
    const res = await updateDAppConfig({
      chainId: dApp.chainId,
      contractAddress: dApp.contractAddress,
      name: name.trim(),
    });
    setSavingName(false);
    if (res.success) {
      setSuccess('Name updated.');
      refresh();
    } else {
      setError(res.error || 'Update failed');
    }
  };

  const handleSaveAbi = async () => {
    if (!dApp || dApp.type !== 'evm') return;
    const result = validateContractAbi(abiText);
    if (!result.valid || !result.parsed) {
      setAbiError(result.error || 'Invalid ABI');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    const res = await updateDAppConfig({
      chainId: dApp.chainId,
      contractAddress: dApp.contractAddress,
      abi: result.parsed,
    });
    setSaving(false);
    if (res.success) {
      setSuccess('ABI updated.');
      setShowAbiEditor(false);
      setAbiError('');
      refresh();
    } else {
      setError(res.error || 'Update failed');
    }
  };

  const handleCancelAbiEdit = () => {
    if (dApp?.abi) {
      setAbiText(JSON.stringify(dApp.abi, null, 2));
      setAbiError('');
      setShowAbiEditor(false);
    } else {
      setAbiText('');
      setAbiError('');
    }
  };

  const handleDeleteDApp = async () => {
    if (!dApp) return;
    setDeleting(true);
    setDeleteError(null);
    const res = await deleteDAppConfig(dApp.chainId, dApp.contractAddress);
    setDeleting(false);
    if (res.success) {
      navigate('/dapps', { replace: true });
    } else {
      setDeleteError(res.error || 'Delete failed');
    }
  };

  const mappingPolicyName = (m: MappingRecord) => (m.policyNames && m.policyNames[0]) || '';

  const clearEdit = (key: string) => {
    setEditingKey((current) => (current === key ? null : current));
    setPolicyDrafts((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const startEdit = (key: string, currentPolicy: string) => {
    setEditingKey(key);
    setPolicyDrafts((prev) => ({ ...prev, [key]: currentPolicy }));
  };

  const handleValidateRow = async (row: {
    key: string;
    methodSig: string | null;
    mapping: MappingRecord | null;
  }) => {
    if (!dApp) return;
    const draft = policyDrafts[row.key] ?? '';
    if (!draft) return;

    const original = row.mapping ? mappingPolicyName(row.mapping) : '';

    // Mapped + unchanged → exit edit, no API call
    if (row.mapping && draft === original) {
      clearEdit(row.key);
      return;
    }

    setSavingKey(row.key);
    setError(null);

    if (row.mapping) {
      // Prefer actionId-only update (works even when ABI / prototype is missing).
      const res = await updateMapping({
        chainId: dApp.chainId,
        contractAddress: dApp.contractAddress,
        actionId: row.mapping.actionId,
        policyNames: [draft],
      });
      setSavingKey(null);
      if (res.success) {
        setSuccess('Mapping updated.');
        clearEdit(row.key);
        refresh();
      } else {
        setError(res.error || 'Failed to update mapping');
      }
      return;
    }

    if (!row.methodSig) {
      setSavingKey(null);
      setError('Cannot map an unknown method.');
      return;
    }

    const prototype = convertMethodSignatureToPrototype(row.methodSig);
    const res = await addMappings([
      {
        chainId: dApp.chainId,
        contractAddress: dApp.contractAddress,
        functionPrototype: prototype,
        policyNames: [draft],
      },
    ]);
    setSavingKey(null);
    if (res.success) {
      setSuccess('Mapping added.');
      clearEdit(row.key);
      refresh();
    } else {
      setError(res.error || 'Failed to add mapping');
    }
  };

  const handleDeleteMapping = async () => {
    if (!dApp || !deleteMappingTarget) return;
    setDeletingMapping(true);
    setDeleteMappingError(null);
    const res = await deleteMapping({
      chainId: dApp.chainId,
      contractAddress: dApp.contractAddress,
      actionId: deleteMappingTarget.actionId,
    });
    setDeletingMapping(false);
    if (res.success) {
      setDeleteMappingTarget(null);
      setSuccess('Mapping deleted.');
      refresh();
    } else {
      setDeleteMappingError(res.error || 'Delete failed');
    }
  };

  const mappingLabel = (m: MappingRecord) =>
    m.functionPrototype || m.instructionSelector || m.actionId;

  const actionIdToMethod = useMemo(() => {
    if (!dApp) return new Map<string, string>();
    return buildActionIdMethodLookup(
      dApp.type,
      String(dApp.chainId),
      dApp.contractAddress,
      methods
    );
  }, [dApp, methods]);

  const mappingRows = useMemo(() => {
    if (!dApp) return [];
    const byActionId = new Map(mappings.map((m) => [m.actionId, m]));
    const seen = new Set<string>();
    const rows: Array<{
      key: string;
      methodSig: string | null;
      actionId: string;
      mapping: MappingRecord | null;
    }> = [];

    for (const method of methods) {
      try {
        const actionId = computeActionId(
          dApp.type,
          String(dApp.chainId),
          dApp.contractAddress,
          method
        );
        seen.add(actionId);
        rows.push({
          key: actionId,
          methodSig: method,
          actionId,
          mapping: byActionId.get(actionId) || null,
        });
      } catch {
        // skip unparseable method signatures
      }
    }

    for (const m of mappings) {
      if (seen.has(m.actionId)) continue;
      const fromLookup = actionIdToMethod.get(m.actionId);
      rows.push({
        key: m.actionId,
        methodSig: fromLookup || m.functionPrototype || null,
        actionId: m.actionId,
        mapping: m,
      });
    }

    return rows;
  }, [dApp, methods, mappings, actionIdToMethod]);

  if (loading) {
    return <div className="loading">Loading dApp…</div>;
  }

  if (!dApp) {
    return (
      <div>
        <div className="dash-error">{error || 'dApp not found'}</div>
        <div style={{ marginTop: '1rem' }}>
          <DashIconButton action="back" title="Back to My dApps" to="/dapps" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="dash-page-header">
        <div className="dash-page-header-row">
          <DashIconButton action="back" title="Back to My dApps" to="/dapps" />
          <div className="dash-page-header-text">
            <h2>{dApp.name}</h2>
            <p>
              <span className={engineTypeBadgeClass(dApp.type)}>{dApp.type}</span>{' '}
              chain {dApp.chainId}
            </p>
          </div>
        </div>
      </div>

      {error && <div className="dash-error">{error}</div>}
      {success && <div className="dash-success">{success}</div>}

      <div className="dash-card">
        <h3>Configuration</h3>
        <div className="dash-form-group">
          <label htmlFor="dapp-name">Name</label>
          <div className="dash-actions" style={{ alignItems: 'stretch' }}>
            <input
              id="dapp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="dash-btn dash-btn-primary"
              disabled={savingName || !name.trim() || name.trim() === dApp.name}
              onClick={handleSaveName}
            >
              {savingName ? 'Saving…' : 'Save name'}
            </button>
          </div>
        </div>
        <div className="dash-form-group">
          <label>Contract address</label>
          <div className="dash-readonly-value">
            <ContractAddress address={dApp.contractAddress} />
          </div>
        </div>
        <div className="dash-form-group">
          <label>Chain ID</label>
          <input value={dApp.chainId} readOnly className="dash-input-readonly dash-mono" tabIndex={-1} />
        </div>

        {dApp.type === 'evm' && (
          <div className="dash-abi-section">
            <div className="dash-actions" style={{ justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <div>
                <strong style={{ fontSize: '0.95rem' }}>Contract ABI</strong>
                <p style={{ margin: '0.25rem 0 0', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  {dApp.abi && dApp.abi.length > 0
                    ? `Loaded · ${methods.length} callable method${methods.length === 1 ? '' : 's'}`
                    : 'Missing — required for method mappings'}
                </p>
              </div>
              <button
                type="button"
                className="dash-btn dash-btn-secondary"
                onClick={() => {
                  if (showAbiEditor) {
                    handleCancelAbiEdit();
                  } else {
                    setShowAbiEditor(true);
                    setAbiError('');
                  }
                }}
              >
                {showAbiEditor ? 'Hide ABI editor' : 'Edit ABI'}
              </button>
            </div>

            {interfaceError && !showAbiEditor && (
              <div className="dash-error">{interfaceError}</div>
            )}

            {showAbiEditor && (
              <>
                <div className="dash-form-group">
                  <label htmlFor="dapp-abi">ABI (JSON)</label>
                  <textarea
                    id="dapp-abi"
                    rows={12}
                    value={abiText}
                    onChange={(e) => handleAbiChange(e.target.value)}
                    placeholder='[{"type":"function","name":"…",…}]'
                    className={abiError ? 'error' : ''}
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                  />
                  {abiError && (
                    <div className="dash-error" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                      There are errors in your ABI: {abiError}
                    </div>
                  )}
                  {!abiError && abiText.trim() && (
                    <div className="dash-success" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                      Your ABI is valid
                    </div>
                  )}
                </div>
                <div className="dash-actions">
                  <button
                    type="button"
                    className="dash-btn dash-btn-primary"
                    disabled={saving || !!abiError || !abiText.trim()}
                    onClick={handleSaveAbi}
                  >
                    {saving ? 'Saving…' : 'Save ABI'}
                  </button>
                  {dApp.abi && dApp.abi.length > 0 && (
                    <button
                      type="button"
                      className="dash-btn dash-btn-secondary"
                      onClick={handleCancelAbiEdit}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {dApp.type === 'stellar' && (
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: 0 }}>
            <p style={{ marginBottom: 0 }}>
              {firewallTargetAddress
                ? 'Trustline Firewall detected — the protected target interface is loaded on-chain via SEP-48'
                : 'Stellar contract interface is loaded on-chain via SEP-48'}
              {interfaceLoading
                ? ' (loading…)'
                : methods.length
                  ? ` (${methods.length} functions)`
                  : ''}
              .
            </p>
            {firewallTargetAddress && (
              <div className="dash-address-row">
                <span className="dash-address-row-label">Protected target</span>
                <ContractAddress address={firewallTargetAddress} />
              </div>
            )}
          </div>
        )}

        {interfaceError && dApp.type === 'stellar' && (
          <div className="dash-error" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
            {interfaceError}
          </div>
        )}
      </div>

      <div className="dash-card">
        <h3>On-chain configuration</h3>
        {interfaceLoading ? (
          <div className="loading">Resolving validation engine…</div>
        ) : !network ? (
          <p className="dash-empty" style={{ margin: 0 }}>
            Unknown network for this dApp — on-chain settings are unavailable.
          </p>
        ) : dApp.type === 'stellar' && !validationEngineAddress ? (
          <p className="dash-empty" style={{ margin: 0 }}>
            Could not resolve the Validation Engine for this dApp.
          </p>
        ) : (
          <OnChainConfigPanel
            compact
            chain={network}
            validationEngineAddress={validationEngineAddress}
            contractAddress={dApp.type === 'evm' ? dApp.contractAddress : undefined}
            desiredTrustline={mappings.length > 0}
          />
        )}
      </div>

      <div className="dash-card">
        <h3>Policy mappings</h3>

        {interfaceLoading && <div className="loading">Resolving contract methods…</div>}

        {policies.length === 0 && (
          <p className="dash-empty" style={{ marginBottom: '0.75rem' }}>
            No policies yet. Create one under My Policies before mapping methods.
          </p>
        )}

        {!interfaceLoading && mappingRows.length === 0 ? (
          <p className="dash-empty">
            No methods available.{' '}
            {dApp.type === 'evm'
              ? 'Provide an ABI above.'
              : 'SEP-48 fetch returned no functions.'}
          </p>
        ) : mappingRows.length > 0 ? (
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th className="dash-actions-cell">Actions</th>
                  <th>Method</th>
                  <th>Action ID</th>
                  <th>Policy</th>
                </tr>
              </thead>
              <tbody>
                {mappingRows.map((row) => {
                  const originalPolicy = row.mapping ? mappingPolicyName(row.mapping) : '';
                  const isEditing = editingKey === row.key;
                  const draft = policyDrafts[row.key] ?? (isEditing ? originalPolicy : '');
                  const saving = savingKey === row.key;
                  const canValidate = Boolean(draft) && !saving && policies.length > 0;
                  const displayPolicy = isEditing ? draft : originalPolicy;
                  const linkedPolicy =
                    policies.find((p) => p.name === originalPolicy) ||
                    (row.mapping?.policyIds?.[0]
                      ? policies.find((p) => p.policyId === row.mapping!.policyIds![0])
                      : undefined);
                  const policyColorKey = displayPolicy
                    ? policies.find((p) => p.name === displayPolicy)?.policyId ||
                      (displayPolicy === originalPolicy
                        ? (row.mapping?.policyIds && row.mapping.policyIds[0]) || displayPolicy
                        : displayPolicy)
                    : '';
                  const rowColor = policyColorKey ? getColorForPolicyId(policyColorKey) : null;
                  const rowStyle = rowColor
                    ? ({
                        ['--mapping-row-bg' as string]: rowColor.bg,
                        ['--mapping-row-accent' as string]: rowColor.text,
                      } as React.CSSProperties)
                    : undefined;

                  return (
                    <tr
                      key={row.key}
                      className={[
                        rowColor ? 'dash-mapping-row-colored' : '',
                        isEditing ? 'dash-mapping-draft-row' : '',
                      ]
                        .filter(Boolean)
                        .join(' ') || undefined}
                      style={rowStyle}
                    >
                      <td className="dash-actions-cell">
                        <div className="dash-actions">
                          {isEditing ? (
                            <DashIconButton
                              action="confirm"
                              title={
                                saving
                                  ? 'Saving…'
                                  : !draft
                                    ? 'Select a policy'
                                    : row.mapping && draft === originalPolicy
                                      ? 'Done'
                                      : 'Validate'
                              }
                              disabled={!canValidate}
                              onClick={() => handleValidateRow(row)}
                            />
                          ) : (
                            <DashIconButton
                              action="edit"
                              title="Edit mapping"
                              disabled={policies.length === 0 || savingKey !== null}
                              onClick={() => startEdit(row.key, originalPolicy)}
                            />
                          )}
                          {row.mapping && (
                            <DashIconButton
                              action="delete"
                              title="Delete mapping"
                              disabled={saving}
                              onClick={() => {
                                setDeleteMappingError(null);
                                setDeleteMappingTarget(row.mapping);
                              }}
                            />
                          )}
                        </div>
                      </td>
                      <td>
                        {row.methodSig ? (
                          <FormattedMethodSignature method={row.methodSig} />
                        ) : (
                          <span className="method-sig-plain" title={row.actionId}>
                            Unknown method
                          </span>
                        )}
                      </td>
                      <td>
                        <span
                          className="dash-mono"
                          title={row.actionId}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {row.actionId.length > 13
                            ? `${row.actionId.slice(0, 8)}…${row.actionId.slice(-4)}`
                            : row.actionId}
                        </span>
                      </td>
                      <td>
                        {isEditing ? (
                          <select
                            className="dash-table-select"
                            value={draft}
                            disabled={policies.length === 0 || saving}
                            onChange={(e) =>
                              setPolicyDrafts((prev) => ({
                                ...prev,
                                [row.key]: e.target.value,
                              }))
                            }
                            aria-label="Policy"
                          >
                            <option value="">Select a policy</option>
                            {policies.map((p) => (
                              <option key={p.policyId} value={p.name}>
                                {p.name}
                              </option>
                            ))}
                            {draft &&
                              !policies.some((p) => p.name === draft) && (
                                <option value={draft}>{draft}</option>
                              )}
                          </select>
                        ) : originalPolicy ? (
                          <span className="dash-policy-chip">
                            <span className="dash-policy-chip-label" title={originalPolicy}>
                              {originalPolicy}
                            </span>
                            {linkedPolicy && (
                              <Link
                                to={`/policies?edit=${encodeURIComponent(linkedPolicy.policyId)}`}
                                className="dash-policy-chip-edit"
                                title={`Edit policy “${linkedPolicy.name}”`}
                                aria-label={`Edit policy ${linkedPolicy.name}`}
                              >
                                <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden>
                                  <path
                                    fill="currentColor"
                                    d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"
                                  />
                                </svg>
                              </Link>
                            )}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <div className="dash-danger-zone">
        <h3>Delete this dApp</h3>
        <p>
          Deletes the dApp configuration and <strong>all nested policy mappings</strong>.
          Shared client policies are kept.
        </p>
        <button
          type="button"
          className="dash-btn dash-btn-danger"
          onClick={() => {
            setDeleteError(null);
            setDeleteOpen(true);
          }}
        >
          Delete dApp
        </button>
      </div>

      <ConfirmDeleteModal
        open={deleteOpen}
        title="Delete this dApp?"
        description={
          <p>
            This removes <strong>{dApp.name}</strong> and all its mappings. Policies at client
            level are preserved.
          </p>
        }
        requiredPhrase="I want to delete this dApp"
        confirmLabel="Delete dApp"
        loading={deleting}
        error={deleteError}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={handleDeleteDApp}
      />

      <ConfirmDeleteModal
        open={!!deleteMappingTarget}
        title="Delete mapping?"
        description={
          <p>
            Remove mapping for <strong>{deleteMappingTarget ? mappingLabel(deleteMappingTarget) : ''}</strong>?
          </p>
        }
        confirmLabel="Delete mapping"
        loading={deletingMapping}
        error={deleteMappingError}
        onCancel={() => setDeleteMappingTarget(null)}
        onConfirm={handleDeleteMapping}
      />
    </div>
  );
};

export default DAppDetailPage;
